import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ColGroupDef, themeQuartz } from "ag-grid-community";
import { Trash2 } from "lucide-react";
import images from "../../../assets/images";
import AddRoundPlanning from "./AddRoundPlanning";
import { roundPlanningType } from "../../../types/api";
import {
  useRoundPlanningStore,
  usePremisesStore,
  useOrgUsersStore,
  useDepartmentStore,
} from "../../../store/store";
import { toast } from "react-toastify";
import { useRoundTypesStore } from "../../../store/masterDataStore";
import { useAuth } from "../../../auth/AuthProvider";
import { usePermissions } from "../../../hooks/usePermission";
import { SAFETY_FACILITY_ROUNDS_PLANNING } from "../../../constants/modules";

const gridTheme = themeQuartz.withParams({
  accentColor: "#93AA9A",
  headerHeight: "200px",
  browserColorScheme: "light",
  headerFontSize: 14,
});

const RoundPlanning: React.FC = () => {
  const { canCreate, canRead, canDelete, canUpdate } = usePermissions();
  const [rowData, setRowData] = useState<roundPlanningType[]>([]);
  const [isModal, setIsModal] = useState<boolean>(false);
  const [mode, setMode] = useState<"create" | "edit" | "view">("create");
  const [selectedData, setSelectedData] = useState<roundPlanningType | null>(
    null,
  );
  const { user } = useAuth();
  const useRole = user?.roles[0];
  const {
    allRoundPlanning,
    loading,
    getAllRoundPlanning,
    createRoundPlanning,
    updateRoundPlanning,
    deleteRoundPlanning,
  } = useRoundPlanningStore();
  const { premiseMap, fetchPremises } = usePremisesStore();
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
  const { roundTypes, fetchAllRoundTypes } = useRoundTypesStore();
  const { departments, fetchAllDepartments } = useDepartmentStore();
  useEffect(() => {
    fetchPremises();
    fetchOrgUsers();
    getAllRoundPlanning();
    fetchAllRoundTypes();
    fetchAllDepartments();
  }, [
    fetchPremises,
    getAllRoundPlanning,
    fetchOrgUsers,
    fetchAllRoundTypes,
    fetchAllDepartments,
  ]);

  useEffect(() => {
    if (allRoundPlanning && Array.isArray(allRoundPlanning)) {
      if (useRole === "orgadmin") {
        setRowData(allRoundPlanning);
      } else if (useRole === "orguser") {
        const filteredData = allRoundPlanning.filter(
          (item: roundPlanningType) => item.assignedRole === user?.id,
        );
        setRowData(filteredData);
      } else {
        setRowData([]);
      }
    } else {
      setRowData([]);
    }
  }, [allRoundPlanning, useRole, user?.id]);
  const getPremiseName = useCallback(
    (premiseId: string) => {
      if (!premiseId) return "";
      return premiseMap?.[premiseId] || "N/A";
    },
    [premiseMap],
  );
  const getRoundType = useCallback(
    (roundTypeId: string) => {
      if (!roundTypeId) return "";
      return (
        roundTypes?.find((type: any) => type._id === roundTypeId)?.roundType ||
        "N/A"
      );
    },
    [roundTypes],
  );
  const getUserName = useCallback(
    (userId: string) => {
      if (!userId) return "";
      return (
        orgUsersData?.find((user: any) => user._id === userId)?.firstName +
        " " +
        orgUsersData?.find((user: any) => user._id === userId)?.lastName ||
        "N/A"
      );
    },
    [orgUsersData],
  );
  const handleView = useCallback(
    (data: roundPlanningType) => {
      if (!canRead) {
        toast.error("You don't have permission to view");
        return;
      }
      setSelectedData(data);
      setMode("view");
      setIsModal(true);
    },
    [canRead],
  );

  const handleEdit = useCallback(
    (data: roundPlanningType) => {
      if (!canUpdate) {
        toast.error("You don't have permission to edit");
        return;
      }
      setSelectedData(data);
      setMode("edit");
      setIsModal(true);
    },
    [canUpdate],
  );

  const handleDelete = useCallback(
    async (id?: string) => {
      if (!id) return;
      if (!canDelete) {
        toast.error("You don't have permission to delete");
        return;
      }
      try {
        await deleteRoundPlanning(id);
      } catch {
        toast.error("Failed to delete round planning");
      }
    },
    [deleteRoundPlanning, canDelete],
  );

  const columnDefs = useMemo<(ColDef<any, any> | ColGroupDef<any>)[]>(
    () => [
      {
        field: "roundName",
        headerName: "Round Name",
        sortable: false,
        flex: 1,
        minWidth: 140,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: { padding: "10px 0 0 5px " },
      },
      {
        field: "roundType",
        headerName: "Round Type",
        sortable: false,
        flex: 1,
        minWidth: 140,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: { padding: "10px 0 0 5px " },
        cellRenderer: (params: any) => getRoundType(params.value || ""),
      },
      {
        field: "Frequency",
        headerName: "Frequency",
        sortable: false,
        flex: 0.8,
        minWidth: 120,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: { padding: "10px 0 0 5px " },
      },
      {
        field: "Premise",
        headerName: "Premise",
        sortable: false,
        flex: 1,
        minWidth: 160,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: { padding: "10px 0 0 5px " },
        cellRenderer: (params: any) => getPremiseName(params.value || ""),
      },
      {
        field: "Area",
        headerName: "Areas",
        sortable: false,
        flex: 1.4,
        minWidth: 180,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: { padding: "10px 0 0 5px " },
        cellRenderer: (params: any) =>
          Array.isArray(params.value)
            ? params.value
              .map(
                (id: string) =>
                  departments?.find((dept: any) => dept._id === id)
                    ?.departmentName,
              )
              .join(", ")
            : "",
      },
      {
        field: "assignedRole",
        headerName: "Assigned Role",
        sortable: false,
        flex: 1,
        minWidth: 150,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: { padding: "10px 0 0 5px " },
        cellRenderer: (params: any) => getUserName(params.value || ""),
      },
      {
        field: "status",
        headerName: "Status",
        sortable: false,
        flex: 0.6,
        minWidth: 110,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: { padding: "10px 0 0 5px " },
        cellRenderer: (params: any) => {
          return (
            <span
              className={`px-2 py-1 text-md font-semibold 
                ${params.data.status === "Scheduled"
                  ? "text-red-500"
                  : params.data.status === "Pending"
                    ? "text-yellow-500"
                    : "text-green-500"
                }`}
            >
              {params.data.status}
            </span>
          );
        },
      },
      {
        field: "action",
        headerName: "Action",
        cellStyle: { padding: "10px 0 0 0 " },
        minWidth: 150,
        headerStyle: { backgroundColor: "#e9f5ff" },
        flex: 1,
        cellRenderer: (params: any) => (
          <div className="flex gap-x-3 pt-2">
            {canRead(SAFETY_FACILITY_ROUNDS_PLANNING) && (
              <img
                onClick={() => handleView(params.data)}
                className="iconTd cursor-pointer"
                src={images.viewIcon}
              />
            )}
            {canUpdate(SAFETY_FACILITY_ROUNDS_PLANNING) && (
              <img
                onClick={() => handleEdit(params.data)}
                className="iconTd cursor-pointer"
                src={images.editIcon}
              />
            )}
            {canDelete(SAFETY_FACILITY_ROUNDS_PLANNING) && (
              <Trash2
                onClick={() => handleDelete(params.data._id)}
                className="cursor-pointer text-red-500"
              />
            )}
          </div>
        ),
        filter: false,
      },
    ],
    [
      getPremiseName,
      handleView,
      handleEdit,
      handleDelete,
      canRead,
      canUpdate,
      canDelete,
    ],
  );

  const theme = useMemo(() => gridTheme, []);

  const defaultColDef = useMemo(
    () => ({
      wrapText: true,
      headerClass: "font-bold text-md bg-gray-100 p-2",
    }),
    [],
  );

  return (
    <>
      <div className="px-2 py-4 mt-14 w-full h-full">
        <div className="bg-white rounded-lg shadow py-6 px-2">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-900 mb-0">
              Round Planning & Scheduling
            </h1>
            {canCreate(SAFETY_FACILITY_ROUNDS_PLANNING) && (
              <button
                onClick={() => {
                  setSelectedData(null);
                  setIsModal(true);
                  setMode("create");
                }}
                className="flex btn-primary roundedBt iconWithBt text-uppercase mr-4"
              >
                ADD ROUND PLANNING
              </button>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
            </div>
          ) : (
            <div
              className="ag-theme-alpine mt-4 w-full"
              style={{ height: "600px", width: "100%" }}
            >
              <AgGridReact
                rowData={rowData}
                columnDefs={columnDefs}
                theme={theme}
                headerHeight={65}
                rowHeight={60}
                defaultColDef={defaultColDef}
              />
            </div>
          )}
        </div>
      </div>

      <AddRoundPlanning
        isOpen={isModal}
        onClose={() => {
          setIsModal(false);
          setSelectedData(null);
        }}
        onSubmit={(data: roundPlanningType) => {
          if (mode === "create") {
            createRoundPlanning(data);
          } else if (mode === "edit" && data._id) {
            updateRoundPlanning(data, data._id);
          }
          setIsModal(false);
          setSelectedData(null);
        }}
        mode={mode}
        initialData={selectedData || undefined}
      />
    </>
  );
};

export default RoundPlanning;
